import { Field, GlassBtn } from "./FlowPieces";
import { Plus, Trash2, AlertTriangle } from "lucide-react";

const Card = ({ title, children, onConfirm, onCancel, saving, testid }) => (
  <div className="cai-glass rounded-[22px] p-4 space-y-2.5 cai-fade-in" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-white/75">{title}</div>
    {children}
    <div className="flex gap-2 pt-1">
      <GlassBtn primary onClick={onConfirm} disabled={saving} data-testid="cai-flow-confirm">
        {saving ? "Guardando…" : "Confirmar"}
      </GlassBtn>
      <GlassBtn onClick={onCancel} data-testid="cai-flow-cancel">Cancelar</GlassBtn>
    </div>
  </div>
);

const Warnings = ({ list }) =>
  list?.length > 0 && (
    <div className="space-y-1">
      {list.map((w, i) => (
        <div key={i} className="flex gap-1.5 text-[11.5px] text-[#FFE3A3]"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w}</div>
      ))}
    </div>
  );

export const PatientDraft = ({ draft, set, ...p }) => {
  const f = (k) => ({ value: draft[k], onChange: (v) => set({ ...draft, [k]: v }), testid: `cai-draft-${k}` });
  return (
    <Card title="Nuevo paciente" testid="cai-draft-patient" {...p}>
      <Field label="Nombre completo" {...f("full_name")} />
      <div className="grid grid-cols-2 gap-2">
        <Field label="Cédula" {...f("document_id")} />
        <Field label="Nacimiento" type="date" {...f("birth_date")} />
        <label className="block">
          <span className="block text-[9.5px] uppercase tracking-[0.16em] text-white/70 mb-1">Sexo</span>
          <select
            value={draft.gender || ""}
            onChange={(e) => set({ ...draft, gender: e.target.value || null })}
            data-testid="cai-draft-gender"
            className="w-full h-9 px-2 rounded-xl bg-white/15 border border-white/25 text-[12.5px] text-white outline-none [&>option]:text-black"
          >
            <option value="">—</option>
            <option value="female">Femenino</option>
            <option value="male">Masculino</option>
          </select>
        </label>
        <Field label="Sangre" {...f("blood_type")} />
        <Field label="Teléfono" {...f("phone")} />
        <Field label="Correo" {...f("email")} />
      </div>
      <Field label="Dirección" {...f("address")} />
      <Field label="Alergias" {...f("allergies")} />
    </Card>
  );
};

export const RxDraft = ({ draft, set, ...p }) => {
  const items = draft.items || [];
  const setItem = (i, k, v) => set({ ...draft, items: items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) });
  return (
    <Card title="Receta" testid="cai-draft-rx" {...p}>
      <Field label="Diagnóstico" value={draft.diagnosis} onChange={(v) => set({ ...draft, diagnosis: v })} testid="cai-draft-diagnosis" />
      {items.map((it, i) => (
        <div key={i} className="rounded-[16px] bg-white/10 p-2.5 space-y-2" data-testid={`cai-draft-item-${i}`}>
          <div className="flex gap-2 items-end">
            <div className="flex-1"><Field label="Medicamento" value={it.medication} onChange={(v) => setItem(i, "medication", v)} /></div>
            <button type="button" onClick={() => set({ ...draft, items: items.filter((_, j) => j !== i) })} className="h-9 w-9 grid place-items-center rounded-xl text-white/80 hover:bg-white/15"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Dosis" value={it.dosage} onChange={(v) => setItem(i, "dosage", v)} />
            <Field label="Frecuencia" value={it.frequency} onChange={(v) => setItem(i, "frequency", v)} />
            <Field label="Duración" value={it.duration} onChange={(v) => setItem(i, "duration", v)} />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => set({ ...draft, items: [...items, { medication: "", dosage: "", frequency: "", duration: "" }] })}
        className="inline-flex items-center gap-1 text-[12px] text-white/90 hover:text-white"
      >
        <Plus className="w-3.5 h-3.5" /> Agregar medicamento
      </button>
      <Warnings list={draft.warnings} />
    </Card>
  );
};

export const ReferralDraft = ({ draft, set, doctor, ...p }) => (
  <Card title={`Referido a ${doctor?.name || ""}`} testid="cai-draft-referral" {...p}>
    <Field label="Motivo" value={draft.reason} onChange={(v) => set({ ...draft, reason: v })} testid="cai-draft-reason" />
    <label className="block">
      <span className="block text-[9.5px] uppercase tracking-[0.16em] text-white/70 mb-1">Notas clínicas</span>
      <textarea
        value={draft.clinical_notes || ""}
        onChange={(e) => set({ ...draft, clinical_notes: e.target.value })}
        rows={3}
        className="w-full px-3 py-2 rounded-xl bg-white/15 border border-white/25 text-[12.5px] text-white outline-none resize-none"
      />
    </label>
    <div className="flex gap-1.5">
      {["rutina", "urgente"].map((u) => (
        <button
          key={u}
          type="button"
          onClick={() => set({ ...draft, urgency: u })}
          data-testid={`cai-draft-urgency-${u}`}
          className={`h-8 px-4 rounded-full text-[12px] capitalize transition-colors ${draft.urgency === u ? "bg-white text-[#5B8FD0]" : "bg-[#2B4A6F]/25 border border-white/35 text-white"}`}
        >
          {u}
        </button>
      ))}
    </div>
  </Card>
);

export const ApptDraft = ({ draft, set, ...p }) => (
  <Card title="Nueva cita" testid="cai-draft-appt" {...p}>
    <div className="grid grid-cols-[1fr_90px] gap-2">
      <Field label="Fecha y hora" type="datetime-local" value={draft.date} onChange={(v) => set({ ...draft, date: v })} testid="cai-draft-date" />
      <Field label="Minutos" type="number" value={draft.duration_min} onChange={(v) => set({ ...draft, duration_min: v })} />
    </div>
    <Field label="Motivo" value={draft.reason} onChange={(v) => set({ ...draft, reason: v })} testid="cai-draft-appt-reason" />
  </Card>
);

export const CertDraft = ({ draft, set, ...p }) => (
  <Card title="Certificado médico" testid="cai-draft-cert" {...p}>
    <Field label="Diagnóstico" value={draft.diagnosis} onChange={(v) => set({ ...draft, diagnosis: v })} testid="cai-draft-cert-diagnosis" />
    <label className="block">
      <span className="block text-[9.5px] uppercase tracking-[0.16em] text-white/70 mb-1">Indicaciones</span>
      <textarea
        value={draft.note || ""}
        onChange={(e) => set({ ...draft, note: e.target.value })}
        rows={3}
        data-testid="cai-draft-cert-note"
        className="w-full px-3 py-2 rounded-xl bg-white/15 border border-white/25 text-[12.5px] text-white outline-none resize-none"
      />
    </label>
    <Field label="Se expide" value={draft.reason} onChange={(v) => set({ ...draft, reason: v })} testid="cai-draft-cert-reason" />
  </Card>
);
